import { useState, type ReactNode } from "react";

import * as RadixTabs from "@radix-ui/react-tabs";
import { Check, ChevronDown } from "lucide-react";

import { Menu, Tabs } from "../../components/ui";
import { resolveWorkflowTabId } from "./workflowTabState";

import tabStyles from "../../components/ui/Tabs/Tabs.module.css";
import styles from "./DocumentationWorkspace.module.css";

export type WorkflowInstanceTab = {
    id: string;
    title: string;
    content: ReactNode;
    workflowSelection?: {
        label: string;
        value: string;
        options: ReadonlyArray<{ id: string; title: string }>;
        disabled: boolean;
        onChange: (workflowId: string) => void;
    };
};

type Props = {
    header: ReactNode;
    tabs: WorkflowInstanceTab[];
};

/**
 * Keep every matched Workflow inside one note card while showing only the
 * employee-selected Workflow's details at a time.
 */
export const WorkflowInstanceSwitcher = ({ header, tabs }: Props) => {
    const [selectedId, setSelectedId] = useState<string>();
    const activeId = resolveWorkflowTabId(
        tabs.map(tab => tab.id),
        selectedId
    );

    if (!activeId) return <>{header}</>;

    return (
        <Tabs value={activeId} onValueChange={setSelectedId} className={styles.workflowSwitcher}>
            <div className={styles.workflowSwitcherHeader}>
                {header}
                <Tabs.List className={styles.workflowTabs}>
                    {tabs.map(tab => {
                        const selection = tab.id === activeId ? tab.workflowSelection : undefined;
                        if (selection) {
                            const title = selection.options.find(option => option.id === selection.value)?.title ?? tab.title;
                            return (
                                <Menu key={tab.id}>
                                    <Menu.Trigger asChild disabled={selection.disabled}>
                                        <RadixTabs.Trigger
                                            value={tab.id}
                                            disabled={selection.disabled}
                                            aria-label={`${selection.label}: ${title}`}
                                            className={`${tabStyles.trigger} ${styles.workflowTab} ${styles.workflowSelectionTab}`}
                                        >
                                            <span>{title}</span>
                                            <ChevronDown size={14} aria-hidden="true" />
                                        </RadixTabs.Trigger>
                                    </Menu.Trigger>
                                    <Menu.Content align="start" className={styles.workflowSelectionMenu}>
                                        {selection.options.map(option => (
                                            <Menu.Item
                                                key={option.id}
                                                disabled={selection.disabled}
                                                icon={option.id === selection.value ? <Check size={16} /> : <span className={styles.workflowOptionSpacer} />}
                                                onClick={() => selection.onChange(option.id)}
                                            >
                                                {option.title}
                                            </Menu.Item>
                                        ))}
                                    </Menu.Content>
                                </Menu>
                            );
                        }
                        return (
                            <Tabs.Trigger key={tab.id} value={tab.id} className={styles.workflowTab}>
                                {tab.title}
                            </Tabs.Trigger>
                        );
                    })}
                </Tabs.List>
            </div>
            {tabs.map(tab => (
                <Tabs.Content key={tab.id} value={tab.id} className={styles.workflowTabContent}>
                    {tab.content}
                </Tabs.Content>
            ))}
        </Tabs>
    );
};
